import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ItemsModuleTabsComponent } from './items-module-tabs/items-module-tabs.component';
import { CommonModule } from '@angular/common';
import { Allergens, Categories, Channels, Components, Departments, GiftcardsTypes, Items, Printers, RawMaterials, Suppliers, Vat } from 'tilby-models';
import { $state, $stateParams, restManager } from 'app/ajs-upgraded-providers';
import { ItemsShowcaseComponent } from './items-tab/items-showcase/items-showcase.component';
import { Transition } from 'angular-ui-router';
import { UpgradeUiViewDirective } from 'src/ajs-to-angular';
import { ItemDataCatalogService, ItemDataSetKey } from '../../services/item-data-catalog.service';
import { CategoriesShowcaseComponent } from './categories-tab/categories-showcase/categories-showcase.component';
import { TranslateService } from '@ngx-translate/core';
import { ItemsModuleSections } from '../../models/items.module';
import { ConfigurationManagerService, EntityBase, EntityManagerService, EntityUpdateInfo, LoaderService, StorageManagerService, StorageUpdateNotification, ToolbarEventsService } from 'src/app/core';
import { ComponentsShowcaseComponent } from './components-tab/components-showcase/components-showcase.component';
import { RawMaterialsShowcaseComponent } from './raw-materials-tab/raw-materials-showcase/raw-materials-showcase.component';
import { DepartmentsShowcaseComponent } from './departments-tab/departments-showcase/departments-showcase.component';
import { VatRatesShowcaseComponent } from './vat-rates-tab/vat-rates-showcase/vat-rates-showcase.component';
import { GridStateService } from '../../services/grid-state.service';
import { filter, merge, Subject, takeUntil } from 'rxjs';
import { CategoryDetailsFormService } from './categories-tab/category-details/category-details-form.service';
import { VariationsFormService } from './items-tab/item-details/item-details-tabs/item-variations-section/variations/variations-form.service';
import { ComponentDetailsFormService } from './components-tab/component-details/component-details-form.service';
import { DepartmentDetailsFormService } from './departments-tab/department-details/department-details-form.service';
import { RawMaterialDetailsFormService } from './raw-materials-tab/raw-material-details/raw-material-details-form.service';
import { VatRateDetailsFormService } from './vat-rates-tab/vat-rate-details/vat-rate-details-form.service';
import { ItemsFormService } from '../../services/items-form.service';
import { ToolbarLayoutService } from 'src/app/shared/services/toolbar-layout.service';
import { sortByName, sortByNumericId } from '../../utils/item.helpers';
import {
  ItemDataOperationsService
} from 'src/app/features/items';

const TRACKED_ENTITIES = ['items', 'categories', 'departments', 'components', 'raw_materials', 'vat'];
@Component({
  selector: 'app-items-module',
  standalone: true,
  imports: [ItemsModuleTabsComponent, CommonModule,
    ItemsShowcaseComponent, UpgradeUiViewDirective,
    CategoriesShowcaseComponent, ComponentsShowcaseComponent,
    RawMaterialsShowcaseComponent, DepartmentsShowcaseComponent,
    VatRatesShowcaseComponent
  ],
  providers: [
    CategoryDetailsFormService,
    VariationsFormService,
    ComponentDetailsFormService,
    DepartmentDetailsFormService,
    RawMaterialDetailsFormService,
    VatRateDetailsFormService,
    GridStateService, ItemsFormService
  ],
  templateUrl: './items-module.component.html',
  styleUrl: './items-module.component.scss'
})
export class ItemsModuleComponent implements OnInit, OnDestroy {

  private readonly stateParams = inject($stateParams);
  private readonly state = inject($state);
  private readonly translate = inject(TranslateService);
  private readonly itemDataCatalogService = inject(ItemDataCatalogService);
  private readonly toolbarEventsService = inject(ToolbarEventsService);
  private readonly itemDataOperationsService = inject(ItemDataOperationsService);
  private readonly gridStateService = inject(GridStateService);
  private readonly loaderService = inject(LoaderService);

  private readonly restManagerService = inject(restManager);
  private entityManagerService = inject(EntityManagerService);
  private readonly storageManagerService = inject(StorageManagerService);
  private readonly configurationManager = inject(ConfigurationManagerService);
  private readonly toolbarLayoutService = inject(ToolbarLayoutService);
  
  private unsubscribeTransition: Function | undefined;
  protected isNavigating = false;

  categoryList: Categories[] = [];
  channelList: Channels[] = [];
  departmentList: Departments[] = [];
  supplierList: Suppliers[] = [];
  itemsList: Items[] = [];
  componentList: Components[] = [];
  rawMaterialList: RawMaterials[] = [];
  vatList: Vat[] = [];
  giftcardsTypes: Array<GiftcardsTypes> = [] = [];
  printers: Array<Printers> = [];
  allergens: Array<Allergens> = [];
  nonFiscalPrinters: Array<Printers> = [];
  documentPrinters: Array<Printers> = [];
  itemsCollectionSize: number = 0;
  
  isLoading = false;
  
  itemsQuery: any;
  scrollIndex: number | null = null;

  modulePath = this.state.$current.name.split('.').pop() === 'showcase';

  tabChange: number = -1;
  tabActive: ItemsModuleSections | null = null;
  onDestroy$ = new Subject<void>();

  private processedNotifications = new Set<string>();
  private isUpdatingInternal = false;

  constructor() {
    this.itemsQuery = this.stateParams.itemsQuery;
    this.scrollIndex = this.stateParams.scrollIndex;

    this.unsubscribeTransition = this.state.router.transitionService.onSuccess({}, (transition: Transition&any) => {
      const stateName = transition.to().name;
      this.modulePath = stateName?.split('.').pop() === 'showcase';
      this.syncTabWithState(stateName);
    })

    merge(
      StorageManagerService.storageUpdates$,
      EntityBase.entityUpdates$
    ).pipe(
      filter(data => {
        if (!data || !TRACKED_ENTITIES.includes(data.entityName)) return false;

        const notificationKey = `${data.entityName}-${data.action}-${data.id || 'massive'}`;

        if (this.processedNotifications.has(notificationKey)) {
          return false;
        }

        if(!data.id && data.action === "DELETED") {
          //Caso modifica massiva in cui scatta prima l'evento DELETED
          return false;
        }

        this.processedNotifications.add(notificationKey);
        setTimeout(() => this.processedNotifications.delete(notificationKey), 500);

        return true;
      }),
      takeUntil(this.onDestroy$)
    ).subscribe(async (data) => {
      if (this.isUpdatingInternal) return; 
      try {
        await this.updateLocalStorageData(data);
      } finally {
        setTimeout(() => this.isUpdatingInternal = false);
      }
    });
  }

  async updateLocalStorageData(data: StorageUpdateNotification | EntityUpdateInfo) {
    if (!data || !data.entityName) return;
    
    const entityName = data.entityName as ItemDataSetKey;
    const isMassiveNotification = !data.entity;
    if(isMassiveNotification) this.isUpdatingInternal = true;

    let currentList = [...(this.itemDataCatalogService.getDataByKey(entityName) || [])] as any[];
    
    switch (data.action) {
      case 'UPDATED':
        if (!isMassiveNotification) {
          const index = currentList.findIndex(item => item.id === data.id);
          
          if (index > -1) {
            currentList[index] = { 
              ...currentList[index], 
              ...(data.entity as any) 
            };
          } else {
            currentList.push(data.entity as any);
          }
          
          currentList = sortByName(currentList);
        } 
        else {
          currentList = await this.refreshEntityCollection(entityName);
        }
        break;

      case 'DELETED':
        if (data.id) {
          currentList = currentList.filter(item => item.id !== data.id);
        }
        else {
          currentList = await this.refreshEntityCollection(entityName);
        }
        break;
    }

    // Aggiorna il catalogo globale
    this.itemDataCatalogService.addDataToCatalog(entityName, currentList);
    this.itemDataCatalogService.registerUpdate(entityName, isMassiveNotification);
  }

  private async refreshEntityCollection(entityName: ItemDataSetKey) {
    let freshData: any = [];

    try {
      switch (entityName) {
        case 'items':
          await this.loadItemsList();
          freshData = this.itemsList;
          break;

        case 'categories':
          freshData = sortByName(await this.entityManagerService.categories.fetchCollectionOffline());
          this.categoryList = freshData;
          this.itemDataCatalogService.updateFilteredDataToCatalog('categories', freshData);
          break;

        case 'departments':
          freshData = sortByName(await this.entityManagerService.departments.fetchCollectionOffline());
          this.departmentList = freshData;
          this.itemDataCatalogService.updateFilteredDataToCatalog('departments', freshData);
          break;

        case 'components':
          freshData = sortByName(await this.entityManagerService.components.fetchCollectionOffline());
          this.componentList = freshData;
          break;

        case 'raw_materials':
          freshData = sortByName(await this.entityManagerService.rawMaterials.fetchCollectionOffline());
          this.rawMaterialList = freshData;
          break;

        case 'vat':
          freshData = sortByNumericId(await this.entityManagerService.vat.fetchCollectionOffline());
          this.vatList = freshData;
          break;
      }
    } catch (error) {
      console.error(`Error refreshing collection for ${entityName}:`, error);
    }
    return freshData;
  }

  async ngOnInit() {
    await this.loadData();
    this.itemDataCatalogService.addDataToCatalog('categories', this.categoryList);
    this.itemDataCatalogService.updateFilteredDataToCatalog('categories', this.categoryList);
    this.itemDataCatalogService.addDataToCatalog('channels', this.channelList);
    this.itemDataCatalogService.updateFilteredDataToCatalog('channels', this.channelList);
    this.itemDataCatalogService.addDataToCatalog('departments', this.departmentList);
    this.itemDataCatalogService.updateFilteredDataToCatalog('departments', this.departmentList);
    this.itemDataCatalogService.addDataToCatalog('suppliers', this.supplierList);
    this.itemDataCatalogService.updateFilteredDataToCatalog('suppliers', this.supplierList);
    this.itemDataCatalogService.addDataToCatalog('items', this.itemsList);
    this.itemDataCatalogService.addDataToCatalog('components', this.componentList);
    this.itemDataCatalogService.addDataToCatalog('raw_materials', this.rawMaterialList);
    this.itemDataCatalogService.addDataToCatalog('vat', this.vatList);
    this.itemDataCatalogService.addDataToCatalog('giftcards-types', this.giftcardsTypes);
    this.itemDataCatalogService.addDataToCatalog('printers', this.printers);
    this.itemDataCatalogService.addDataToCatalog('allergens', this.allergens);
    this.itemDataCatalogService.addDataToCatalog('non-fiscal-printers', this.nonFiscalPrinters);
    this.itemDataCatalogService.addDataToCatalog('document-printers', this.documentPrinters);
    this.itemDataCatalogService.isReady.set(true);
    this.syncTabWithState(this.state?.current?.name);
  }

  async loadData() {
    this.loaderService.show();
    this.isLoading = true;

    try {
      // Caricamento dati veloci (offline)
      const [
        cats, depts, comps, raws, vats, sups, printers, allergens, nonFiscalPrinters, documentPrinters, size
      ] = await Promise.all([
        this.entityManagerService.categories.fetchCollectionOffline(),
        this.entityManagerService.departments.fetchCollectionOffline(),
        this.entityManagerService.components.fetchCollectionOffline(),
        this.entityManagerService.rawMaterials.fetchCollectionOffline(),
        this.entityManagerService.vat.fetchCollectionOffline(),
        this.entityManagerService.suppliers.fetchCollectionOffline(),
        this.entityManagerService.printers.fetchCollectionOffline(),
        this.entityManagerService.allergens.fetchCollectionOffline(),
        this.entityManagerService.printers.fetchCollectionOffline({ type: 'nonfiscal' }),
        this.entityManagerService.printers.fetchCollectionOffline({ type_in: ['fiscal', 'rt', 'receipt'] }),
        this.storageManagerService.getCollectionSize('items')
      ]);

      // this.loaderService.hide();
      // await new Promise(resolve => setTimeout(resolve, 500000))

      // Assegnazione e ordinamento immediato
      this.categoryList = sortByName(cats);
      this.departmentList = sortByName(depts);
      this.componentList = sortByName(comps);
      this.rawMaterialList = sortByName(raws);
      this.vatList = sortByNumericId(vats);
      this.supplierList = sups;
      this.printers = printers;
      this.allergens = allergens;
      this.nonFiscalPrinters = nonFiscalPrinters;
      this.documentPrinters = documentPrinters;
      this.itemsCollectionSize = size;

      // Caricamento dati lenti (online / complessi)
      await Promise.all([
        this.loadItemsList(),
        this.loadChannels(),
        this.loadGiftcards()
      ]);

    } catch (error) {
      console.error("Errore durante il caricamento dei dati:", error);
    } finally {
      this.loaderService.hide();
      this.isLoading = false;
    }
  }

  private async loadItemsList() {
    try {
      const [localItems, onlineItems] = await Promise.all([
        this.entityManagerService.items.fetchCollectionOffline(),
        this.entityManagerService.items.fetchCollectionOnline({ on_sale: false }, true)
      ]);
      
      // Map per ID per evitare duplicati se un prodotto dovesse apparire in entrambe le liste
      const combinedMap = new Map();
      localItems.forEach(item => combinedMap.set(item.id, item));
      (onlineItems as any[]).forEach(item => combinedMap.set(item.id, item));
      
      this.itemsList = sortByName(Array.from(combinedMap.values()));

    } catch (e) {
      // console.error("Errore online items ", e);
    }
  }

  private async loadChannels() {
    this.channelList = await this.entityManagerService.channels.fetchCollectionOnline() as Channels[];
  }

  private async loadGiftcards() {
    if (this.configurationManager.isModuleEnabled("giftcard")) {
      try {
        const result = await this.restManagerService.getList("giftcards_types");
        this.giftcardsTypes = Array.isArray(result) ? result : [];
      } catch {
        this.giftcardsTypes = [];
      }
    }
  }

  private syncTabWithState(stateName: string) {
    if (!stateName) return;
    this.toolbarLayoutService.showModuleTitle$.next(false);
    
    let newTabChange = -1;
    let newTabActive: ItemsModuleSections | null = null;
    let moduleTitleText = "";

    if (stateName.includes('categories')) {
      newTabActive = 'categories';
      newTabChange = 1;
      moduleTitleText = "CATEGORIES";
    } else if (stateName.includes('components')) {
      newTabActive = 'components';
      newTabChange = 2;
      moduleTitleText = "COMPONENTS";
    } else if (stateName.includes('raw-materials')) {
      newTabActive = 'raw-materials';
      newTabChange = 3;
      moduleTitleText = "RAW_MATERIALS";
    } else if (stateName.includes('departments')) {
      newTabActive = 'departments';
      newTabChange = 4;
      moduleTitleText = "DEPARTMENTS";
    } else if (stateName.includes('vat-rate-management')) { 
      newTabActive = 'vat_rates';
      newTabChange = 5;
      moduleTitleText = "VAT_RATE";
      this.toolbarEventsService.moduleTitle.next("VAT_RATE");
    } else if (stateName.includes('items')) {
      newTabActive = 'items';
      newTabChange = 0;
      moduleTitleText = "ITEMS";
    }

    if (this.tabChange !== newTabChange) {
      this.tabActive = newTabActive;
      this.tabChange = newTabChange;
      if(moduleTitleText) {
        this.toolbarEventsService.moduleTitle.next(moduleTitleText);
      }
    }
  }

  changeTab(textLabelPanel: any) {
    if (this.isLoading || this.isNavigating || this.state.router.globals.transition) {
      // console.log("changeTab: navigazione bloccata (isNavigating o isLoading o || this.state.router.globals.transition)");
      return;
    }

    let targetSection: ItemsModuleSections | null = null;
    let targetRoute: string | null = null;

    if (textLabelPanel === this.translate.instant("ITEMS.TOPBAR_ITEMS.PRODUCTS")) {
      targetSection = 'items';
      targetRoute = 'app.new.items.showcase';
    }
    else if (textLabelPanel === this.translate.instant("ITEMS.TOPBAR_ITEMS.CATEGORIES")) {
      targetSection = 'categories';
      targetRoute = 'app.new.items.categories.showcase';
    }
    else if (textLabelPanel === this.translate.instant("ITEMS.TOPBAR_ITEMS.COMPONENTS")) {
      targetSection = 'components';
      targetRoute = 'app.new.items.components.showcase';
    }
    else if (textLabelPanel === this.translate.instant("ITEMS.TOPBAR_ITEMS.RAW_MATERIALS")) {
      targetSection = 'raw-materials';
      targetRoute = 'app.new.items.raw-materials.showcase';
    }
    else if (textLabelPanel === this.translate.instant("ITEMS.TOPBAR_ITEMS.DEPARTMENTS")) {
      targetSection = 'departments';
      targetRoute = 'app.new.items.departments.showcase';
    }
    else if (textLabelPanel === this.translate.instant("ITEMS.TOPBAR_ITEMS.VAT_RATE")) {
      targetSection = 'vat_rates';
      targetRoute = 'app.new.items.vat-rate-management.showcase';
    }

    if (this.tabActive === targetSection) {
      // console.log("Tab già attivo, blocco navigazione ridondante");
      return;
    }

    this.isNavigating = true;
    this.tabActive = targetSection; 

    this.state.go(targetRoute).finally(() => {
      setTimeout(() => {
        this.isNavigating = false;
      });
    });
  }

  ngOnDestroy(): void {
    if (this.unsubscribeTransition) {
      this.unsubscribeTransition();
    }
    this.itemDataOperationsService.resetIsLeanPmsEnabled();
    this.itemDataOperationsService.resetChainProduction();
    this.gridStateService.clearAllStates();
    this.toolbarLayoutService.showModuleTitle$.next(true);

    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
